import React from 'react';
import {History, Location} from "history";
import Alert from "react-bootstrap/Alert";
import SomlService from "../../services/soml-service";
import {Schema} from "../../types/Schema";
import {getQueryParam} from '../../utils/location-utils';
import Loader from "../../components/loader/Loader";
import {DialogService} from "../../common/Dialog";
import {UnsavedChangesConfirmation} from "../../common/NavigationConfirmation";

import SelectMenu, {OptionProps} from "../../components/select/SelectMenu";
import CreateSchemaAction from "../../components/schema/actions/CreateSchemaAction";
import GenerateSchemaAction from "../../components/schema/actions/GenerateSchemaAction";
import UploadSchemaAction from "../../components/schema/actions/UploadSchemaAction";
import SchemaPlayground from "../../components/schema/playground/SchemaPlayground";
import Notification from "../../common/Notification";
import ContentBlocker from "../../components/context/ContentBlocker";
import {WorkbenchStateContext} from "../../components/context/WorkbenchContext";

import './SchemaPlaygroundPage.scss';

const MAX_SCHEMAS_PAGE_SIZE = 100;

type SchemaPlaygroundPageProps = {
  location: Location,
  history: History
}

type SchemaPlaygroundPageState = {
  selectedSchema?: string;
  schemas: Array<Schema>;
  hasLoaded: boolean;
  hasSchemas: boolean;
  hasChanges: boolean;
}

export default function SchemaPlaygroundPage(props: SchemaPlaygroundPageProps) {
  return (
    <div className="SchemaPlaygroundPage">
      <h1>Playground</h1>
      <ContentBlocker checkReadPermission={true}>
        <SchemaPlaygroundWrapper {...props} />
      </ContentBlocker>
    </div>
  );
}

// TODO: back/forward are not working
// TODO: clicking 2nd time on playground menu item clears the query param
class SchemaPlaygroundWrapper extends React.Component<SchemaPlaygroundPageProps, SchemaPlaygroundPageState> {

  static contextType = WorkbenchStateContext;

  constructor(props: any) {
    super(props);
    this.state = {
      schemas: [],
      selectedSchema: this.getSchemaIdQueryParam(),
      hasLoaded: false,
      hasSchemas: false,
      hasChanges: false,
    };
    this.onSchemaMenuChange = this.onSchemaMenuChange.bind(this);
    this.onSchemaChange = this.onSchemaChange.bind(this);
  }

  private getSchemaIdQueryParam() {
    return getQueryParam(this.props.location, 'id') as string;
  }

  componentDidMount() {
    this.loadSchemas();
  }

  private loadSchemas() {
    SomlService.getAllSchemas(MAX_SCHEMAS_PAGE_SIZE)
      .then((schemas: Array<Schema>) => this.updateStateWithSchemasResponse(schemas))
      .catch((err: any) => this.handleSchemasLoadingError(err))
      .finally(() => {
        this.setState({
          hasLoaded: true
        });
      });
  }

  private handleSchemasLoadingError(err: any): void {
    const errMsg = err ? (err.message || err) : '';
    const reason = err ? ` Reason: ${errMsg}` : '';
    const message = `Cannot load SOML schemas!${reason}`;
    Notification.error({message});
  }

  private updateStateWithSchemasResponse(schemas: Array<Schema>): Promise<void> {
    const defaultSchema = this.getDefaultSchema(schemas);
    this.setState({
      schemas: schemas,
      selectedSchema: defaultSchema,
      hasSchemas: schemas.length > 0
    });
    this.updateSchemaIdQueryParam(defaultSchema);
    return Promise.resolve();
  }

  private getDefaultSchema(schemas: Array<Schema>): string | undefined {
    if (this.state.selectedSchema) {
      return this.state.selectedSchema;
    }
    if (schemas.length > 0) {
      return schemas[0].id;
    }
    return undefined;
  }

  private onSchemaMenuChange(selectedSchema: string) {
    if (this.state.hasChanges) {
      DialogService.confirm({
        message: 'You have unsaved changes. Are you sure you want to change the schema?',
        onConfirm: () => this.changeSchema(selectedSchema),
      });
    } else {
      this.changeSchema(selectedSchema);
    }
  }

  private changeSchema(schemaId: string) {
    this.setState({
      selectedSchema: schemaId,
      hasChanges: false
    }, () => {
      // Updating the URL after "hasChanges: false" has settled in
      this.updateSchemaIdQueryParam(schemaId);
    });
  }

  private updateSchemaIdQueryParam(id: string | undefined) {
    if (this.getSchemaIdQueryParam() !== id) {
      this.props.history.push({
        search: `?id=${id}`
      });
    }
  }

  private onSchemaChange(schema: string, dirty: boolean) {
    this.setState({
      hasChanges: dirty
    });
  }

  render() {
    const isLoading = !this.state.hasLoaded;
    const hasSchemas = this.state.hasSchemas;

    if (isLoading) {
      return <Loader size="lg" block={true}/>;
    } else if (!isLoading && !hasSchemas) {
      const forbidden = !this.context.canCreate();
      return (
        <>
          <Alert variant="info" className="NoSchemasAlert">No schemas</Alert>
          <div className="SchemaPlaygroundPageActions">
            <CreateSchemaAction forbidden={forbidden}/>
            <GenerateSchemaAction forbidden={forbidden}/>
            <UploadSchemaAction standardButton={true} forbidden={forbidden}/>
          </div>
        </>
      );
    } else {
      return (
        <>
          <UnsavedChangesConfirmation when={this.state.hasChanges}/>
          <SchemasSelectMenu schemas={this.state.schemas}
                             selected={this.state.selectedSchema}
                             onChange={this.onSchemaMenuChange}/>
          <SchemaPlayground schemaId={this.state.selectedSchema}
                            onChange={this.onSchemaChange}/>
        </>
      );
    }
  }

}

function SchemasSelectMenu(props: {
  schemas: Array<Schema>,
  selected: string | undefined,
  onChange: (selection: string) => void
}) {
  const options: Array<OptionProps> = props.schemas.map(schema => {
    return {
      id: schema.properties.id,
      label: schema.properties.label || schema.properties.id
    };
  });
  return (
    <SelectMenu className="SchemaSelectMenu"
                options={options}
                selected={props.selected}
                onChange={props.onChange}/>
  );
}
